import React, { useState } from 'react';
import { withStyles, Button, CircularProgress } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import {
  SEARCH_FILTER_OPTIONS,
  SEARCH_TYPE,
  SEARCH_INPUT_NUMBER_VALIDATION,
  SEARCH_INPUT_ALPHA_NUMERIC_VALIDATION
} from '../constants';
import CustomTextField from '../../../Dispatch/RouteExecution/DriverPopUp/common/CustomTextField';
import DateTimeEditor from './DateTimeEditor';
import appConstants from '../../../../../appConstants';
import { getUTCDate, formatMonthDateYear } from '@dataGrid/gridUtilities';
import { isValid as isDateTimeValid, format } from 'date-fns';

const styles = (theme) => ({
  searchButton: {
    fontSize: theme.textFontSize,
    flexGrow: 1,
    borderRadius: 'unset',
    color: theme.TextColor,
    '&:focus': {
      border: `1px solid ${theme.HeaderSelectedText}`,
      background: 'rgba(0, 0, 0, 0.08)'
    },
    border: `1px solid ${theme.AppBGColor}`,
  },

  marginNormal: { margin: 0, width: '100%' },
  inputField: {
    padding: 8,
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    height: 'initial'
  },
  activeSelection: {
    fontWeight: 600,
    color: theme.white,
    backgroundColor: theme.HeaderSelectedText,
    "&:hover": {
      backgroundColor: theme.HeaderSelectedText,
    },
    "&:focus": {
      backgroundColor: theme.HeaderSelectedText,
      opacity: 0.8,
    },
  },
  filterContainer: {
    display: 'flex',
    marginBottom: 16
  },
  button: {
    width: '15%',
    marginLeft: 4,
    height: 34,
    '&$disabledButton,&$disabledButton:hover': {
      cursor: 'not-allowed',
      pointerEvents: 'all',
      backgroundColor: theme.customInputBGColor,
      color: theme.white
    }
  },
  searchBoxWrapper: {
    width: '80%',
    display: 'flex'
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  circularProgress: {
    color: theme.white
  },
  container: {
    width: '100%',
    minHeight: '14%'
  },
  header: {
    color: theme.iconColor,
    fontSize: theme.textFontSize,
    fontWeight: 500,
    marginTop: 24,
    marginBottom: 12,
    display: 'block'
  },
  inputBox: {
    width: '70%',
    paddingLeft: '10px'
  },
  fullInputBox: {
    width: '100%'
  },
  labelClass: {
    lineHeight: '8px',
    color: theme.TextColor,
    transform: 'translate(14px, 14px) scale(1)',
    fontSize: theme.headerTextFontSize,
    opacity: 0.7
  },
  disabledButton: {
    cursor: 'not-allowed',
    pointerEvents: 'all',
    backgroundColor: theme.iconColor,
    color: theme.white
  }
});

const SearchBar = ({ classes, showLoader, handleSearch }) => {
  const [searchBy, setSearchBy] = useState(SEARCH_TYPE.DriverNumber);
  const [dispatchDate, setDispatchDate] = useState(new Date());
  const [searchInputValue, setSearchInputValue] = useState('');

  const handleSearchChange = (value) => {
    setSearchInputValue(value);
  };

  const handleFilterChange = (value) => () => {
    if (value !== searchBy) {
      setSearchBy(value);
      handleSearchChange('');
    }
  };

  const onDateChanged = (value) => {
    if (
      value &&
      isDateTimeValid(value) &&
      format(getUTCDate(dispatchDate), appConstants.APP_DATE_FORMAT) !==
        format(getUTCDate(value), appConstants.APP_DATE_FORMAT)
    ) {
      setDispatchDate(value);
    }
  };

  const onSearch = () => {
    handleSearch(searchBy, searchInputValue, dispatchDate);
  };

  const inputLabel = SEARCH_FILTER_OPTIONS.find((option) => option.value === searchBy);

  const isSearchButtonDisabled = () => {
    if (searchBy === SEARCH_TYPE.DriverNumber) {
      return dispatchDate && isDateTimeValid(dispatchDate) && !Boolean(searchInputValue);
    }
    return !Boolean(searchInputValue);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !showLoader && !isSearchButtonDisabled()) {
      onSearch();
    }
  };

  return (
    <div className={classes.container}>
      <span className={classes.header}>SEARCH BY</span>
      <div className={classes.filterContainer}>
        {SEARCH_FILTER_OPTIONS.map((option) => (
          <Button
            className={`${classes.searchButton} ${
              searchBy === option.value ? classes.activeSelection : ''
            }`}
            key={option.label}
            variant="outlined"
            onClick={handleFilterChange(option.value)}
            disableRipple>
            {option.label}
          </Button>
        ))}
      </div>
      <div className={classes.searchContainer}>
        <div className={classes.searchBoxWrapper}>
          {searchBy === SEARCH_TYPE.DriverNumber && (
            <DateTimeEditor
              dateType={appConstants.DATETYPES.MONTHDATEYEAR}
              onChange={onDateChanged}
              disableFuture={true}
              value={formatMonthDateYear({
                value: dispatchDate
              })}
            />
          )}
          <div
            className={
              searchBy === SEARCH_TYPE.DriverNumber ? classes.inputBox : classes.fullInputBox
            }>
            <CustomTextField
              className={classes.fullWidth}
              id="searchBy"
              handleChange={handleSearchChange}
              value={searchInputValue}
              error={null}
              label={inputLabel?.label || searchBy}
              mainClassProps={{ marginNormal: classes.marginNormal }}
              InputPropsClasses={{ input: classes.inputField }}
              labelClassProps={{ root: classes.labelClass }}
              inputProps={
                searchBy === SEARCH_TYPE.DriverNumber
                  ? SEARCH_INPUT_ALPHA_NUMERIC_VALIDATION
                  : SEARCH_INPUT_NUMBER_VALIDATION
              }
              onKeyPress={handleKeyPress}
            />
          </div>
        </div>
        <Button
          color="primary"
          variant="contained"
          disabled={showLoader || isSearchButtonDisabled()}
          classes={{ root: classes.button, disabled: classes.disabledButton }}
          onClick={onSearch}>
          {showLoader ? (
            <CircularProgress size={16} classes={{ root: classes.circularProgress }} />
          ) : (
            <SearchIcon />
          )}
        </Button>
      </div>
    </div>
  );
};

export default withStyles(styles)(SearchBar);
